import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { ModalTransition } from "../../../common/components/transitions";
import StyledDialogTitle from "../../../common/components/dialog/dialog-title";
import StyledDialogActions from "../../../common/components/dialog/dialog-actions";
import { commonButtonProps } from "../../../common/components/buttons/sxprops";
import useMainStore from "../../landing-page/api/store";
import useUserManagementStore from "../api/store";
import { useGetUserById } from "../api/hooks";
import { Handlers } from "../api/handlers";
import { checkBoxPr } from "../../clients/components/filter-menu/sxprops";

interface AddGroupsModalProps {
  isInternal: boolean;
}

export const AddGroupsModal: React.FC<AddGroupsModalProps> = ({ isInternal }) => {

  const clientsFromStore = useMainStore((s) => s.currentUser?.clients);
  const selectedClient = useMainStore((s) => s.selectedClient);

  const clients = clientsFromStore ?? [];
  const selectedIds = useUserManagementStore((s) => s.selectedGroupClientIds);
  const setSelectedIds = useUserManagementStore(
    (s) => s.setSelectedGroupClientIds
  );

  const isAddGroupAdminModalOpen = useUserManagementStore(
    (s) => s.isAddGroupAdminModalOpen
  );
  const isEditGroupAdminModal = useUserManagementStore(
    (s) => s.isEditGroupAdminModal
  );

  const allSelected =
    clients.length > 0 && selectedIds.length === clients.length;

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(clients.map((c) => c.id));
    }
  };

  const { data: userForEdit, isSuccess: hasLoadedUser } = useGetUserById(isInternal);

  useEffect(() => {
    if (hasLoadedUser && userForEdit?.clientIds && isEditGroupAdminModal) {
      setSelectedIds(userForEdit.clientIds);
    }
  }, [, hasLoadedUser, userForEdit, setSelectedIds]);

  useEffect(() => {
    if (isAddGroupAdminModalOpen && !isEditGroupAdminModal && selectedClient) {
      setSelectedIds([selectedClient.id]);
    }
  }, [
    isAddGroupAdminModalOpen,
    isEditGroupAdminModal,
    selectedClient,
    setSelectedIds,
  ]);

  const handlers = new Handlers();

  return (
    <Dialog
      open={isAddGroupAdminModalOpen || isEditGroupAdminModal}
      onClose={handlers.handleCloseGroupModal}
      fullWidth
      slots={{ transition: ModalTransition }}
      slotProps={{
        paper: {
          component: "form",
          sx: {
            borderRadius: "24px",
            maxWidth: 900,
            width: "100%",
          },
        },
      }}
      aria-labelledby="add-groups-modal-title"
    >
      <StyledDialogTitle
        actionType="close"
        title="Invite Co-Group Admin"
        handleClose={handlers.handleCloseGroupModal}
      />

      <DialogContent sx={{ mt: 2, px: 3 }}>
        <Typography sx={{ mb: 2, color: "#6D87AA", fontSize: "16px" }}>
          Select one or more Group where the user will be assigned as a co-group
          admin{" "}
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                onChange={handleSelectAll}
                sx={checkBoxPr}
              />
            }
            label="Select All"
          />
          {clients.map((client) => (
            <FormControlLabel
              key={client.id}
              control={
                <Checkbox
                  checked={selectedIds.includes(client.id)}
                  onChange={() => handleToggle(client.id)}
                  sx={checkBoxPr}
                />
              }
              label={client.clientBasicInfo.companyName}
            />
          ))}
        </FormGroup>
      </DialogContent>

      <StyledDialogActions>
        <Button onClick={handlers.handleCloseGroupModal}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (hasLoadedUser && userForEdit?.clientIds) {
              handlers.handleContinueEdit();
            } else {
              handlers.handleContinue();
            }
          }}
          disabled={selectedIds.length === 0}
          sx={{
            ...commonButtonProps,
            backgroundColor: "primary.main",
          }}
        >
          Continue
        </Button>
      </StyledDialogActions>
    </Dialog>
  );
};
